import { createElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { FunctionUtils } from "../../../../Global/Base/Utils/FunctionUtils";
import { safeParseInt } from "../../../UI.React/utils/parse";
import { FIT_TO_CONTENT_MARGIN } from "../constants";
import { AlignPanel } from "../XGControls/AlignPanel";
import { BaseAlignElement } from "../XGControls/AlignPanels/BaseAlignElement";
import { ControlElement } from "../XGControls/AlignPanels/ControlElement";
import { Control } from "./Control";
import { StretchType } from "./ControlEnums";
import { ControlParams, IHTMLElementWithControl } from "./ControlInterfaces";

export class GroupBox extends Control {
    private readonly _stretchType: StretchType;
    private readonly _children: Control[] = [];
    private readonly _resizeGroupBoxDebounced: () => void;
    private readonly _groupBoxContainer: HTMLElement;
    private _alignPanel: AlignPanel;
    private _stylesheet: HTMLStyleElement;

    public override set visible(value: boolean) {
        if (this.element == undefined)
            return;

        super.visible = value;

        if (this._groupBoxContainer) // skrývání kontejneru při skrytí GB
            this._groupBoxContainer.style.display = value ? "" : "none";
    }

    public override get visible(): boolean {
        return super.visible;
    }

    constructor(params: ControlParams) {
        super(params);
        const { element: groupBoxElement } = params;

        // v pripade umisteni GroupBoxu do VerticalScrollVieweru bez AlignPanelu nedojde k obaleni GroupBoxu containerem, je nutne doplnit
        if (!this.element.parentElement.classList.contains("GroupBoxContainer")) {
            this._stylesheet = createElement({ type: "style", id: `GroupBoxStyles-${groupBoxElement.id}` }) as HTMLStyleElement;
            document.head.appendChild(this._stylesheet);

            const previousElement = this.element.previousElementSibling;
            const groupBoxParent = this.element.parentElement;
            this._groupBoxContainer = new ControlElement(this.element, this._stylesheet).element; // toto dost nečekaně vezme předaný element, odstraní ho z DOMu a

            if (previousElement) // je potřeba zachovat pozici elementu v DOM struktuře, takže ho dát za stejného sourozence a ne až na konec (chyba vzniká například když je ve VSV kombinace AP a GB - GB se všechny přemístí na konec)
                previousElement.insertAdjacentElement("afterend", this._groupBoxContainer);
            else
                groupBoxParent.insertAdjacentElement("afterbegin", this._groupBoxContainer);
        } else {
            this._groupBoxContainer = this.element.parentElement;
        }

        const label = ControlElement.getCaptionElement(this.element.id, this.element.getAttribute("data-captionLocKey"));
        const captionControl = document.createElement("div");
        captionControl.classList.add("GroupBoxCaption");
        captionControl.appendChild(label.element);
        this._groupBoxContainer.appendChild(captionControl);
        this.element.setAttribute("aria-labelledby", label.element.id); // kvůli přístupnosti se propojí přes aria-labelledby, který obsahuje id labelu pro GroupBox
        this.element.setAttribute("role", "group"); // nastavení správné role, kvůli přístupnosti
        this.element.classList.add("GroupBox");
        this._stretchType = BaseAlignElement.parseStretchType(this.element.getAttribute("data-stretch"));

        // je 1. potomek GroupBoxu GridLayout
        const isGroupBoxFirstChildGridLayout = this.element.children[0]?.getAttribute("data-tsccontrol") === "GridLayout";
        // je GroupBox potomek GridLayoutu
        const isGroupBoxGridLayoutChild = this.element.parentElement?.parentElement?.classList.contains("mount-html-element-component");

        if ((this._stretchType === StretchType.vertical || this._stretchType === StretchType.both) && (isGroupBoxFirstChildGridLayout || isGroupBoxGridLayoutChild)) {
            // Ošetření situace, kdy má GroupBox nastaveno Stretch=stVertical nebo StretchType.both a jeho 1. potomek je GridLayout nebo je potomkem GridLayoutu
            this._setGroupBoxHeightInGridLayout(isGroupBoxFirstChildGridLayout, isGroupBoxGridLayoutChild);
        } else {
            this._alignPanel = new AlignPanel(params);
        }

        this._resizeGroupBoxDebounced = FunctionUtils.debouncedFunction(() => this._resizeGroupBox(), 100);
        this._initializeChildren();
    }

    /**
     * Nastavuje výšku GroupBoxu, kdy je 1. potomek GroupBoxu GridLayout nebo je GroupBox potomkem GridLayoutu
     */
    private _setGroupBoxHeightInGridLayout(isGroupBoxFirstChildGridLayout: boolean, isGroupBoxGridLayoutChild: boolean): void {
        if (isGroupBoxFirstChildGridLayout) { // pokud je 1. potomek GroupBoxu GridLayout, tak se jeho výška nastaví pomocí stylů
            this.element.setAttribute("style", "height:auto !important");
            this.element.style.position = "relative";
            this.element.style.maxHeight = "100vh";
            this.element.style.paddingTop = `${FIT_TO_CONTENT_MARGIN}px`;
            this.element.style.paddingBottom = `${FIT_TO_CONTENT_MARGIN}px`;
        } else if (isGroupBoxGridLayoutChild && !isGroupBoxFirstChildGridLayout) { // pokud je GroupBox potomkem GridLayoutu, musí se výška dopočítat podle jeho potomků
            const children = Array.from(this.element.children) as HTMLElement[];
            const childrenHeights: number[] = []; // pole s výškami všech potomků GroupBoxu

            children.forEach(child => childrenHeights.push(safeParseInt(child.dataset.top) + safeParseInt(child.dataset.height)));
            const groupBoxHeight = Math.max(...childrenHeights.filter(Boolean)) + FIT_TO_CONTENT_MARGIN;
            this.element.setAttribute("style", "min-height:" + `${groupBoxHeight}` + "px !important");
            this.element.style.maxHeight = "100vh";

            this._alignPanel = new AlignPanel({ element: this.element, originalRightsDefinition: this.originalRightsDefinition });
        }
    }

    /**
     * Projde potomky a zaregistruje udalost visibilityChanged pro pripadne prepocitani rozmeru groupboxu.
     */
    private _initializeChildren(): void {
        if (this._stretchType === StretchType.none) //pokud se box nestretchuje, neni potreba hledat viditelnost komponent uvnitr
            return;

        const childElements = Array.from(this.element.querySelectorAll("[data-tsccontrol][data-attrname]")) as IHTMLElementWithControl[];
        for (const ctrl of childElements) {
            // ReSharper disable once Html.EventNotResolved
            this._events.addListener(ctrl, "controlRegistered", (e: CustomEvent) => {
                if (e.detail instanceof Control) {
                    this._children.push(e.detail);

                    this._events.add(e.detail.visibilityChanged, () => {
                        this._resizeGroupBoxDebounced();
                    });
                }
            });
        }
    }

    private _resizeGroupBox(): void {
        if (!this.visible)
            return;
        const visibleChildren = this._children.filter(i => i.visible).map(i => new BaseAlignElement(i.element));

        const size = ControlElement.getContentSize(this.element, this._stretchType, visibleChildren);

        if (size.width)
            this.element.style.width = size.width + "px";
        if (size.height)
            this.element.style.height = size.height + "px";
    }

    public override dispose(): void {
        if (this._isDisposed) {
            return;
        }
        this._alignPanel?.dispose();
        this._stylesheet?.remove();
        super.dispose();
    }
}
